export interface Category {
  slug: string;
  name: string;
  intro: string;
  icon: string;
  subs: string[];
}

export interface Topic {
  id: string;
  title: string;
  intro: string;
  cover: string;
  reason: string;
}

export interface Article {
  id: string;
  title: string;
  summary: string;
  content: string[];
  quote: string;
  category: string;
  subCategory: string;
  author: string;
  date: string;
  image: string;
  tags: string[];
  views: number;
  hot: boolean;
  topicIds: string[];
}

export const categories: Category[] = [
  {
    slug: "society",
    name: "社会",
    intro: "聚焦民生百态、公共安全与法治进程，追踪城市里最真实的一线现场。",
    icon: "ri-government-line",
    subs: ["治安", "民生", "法治"],
  },
  {
    slug: "entertainment",
    name: "娱乐",
    intro: "影视、音乐、明星动态背后的行业逻辑，还原聚光灯之外的真相。",
    icon: "ri-movie-2-line",
    subs: ["影视", "明星", "音乐"],
  },
  {
    slug: "tech",
    name: "科技",
    intro: "互联网、人工智能与硬件前沿的深度解读，看清技术如何改变生活。",
    icon: "ri-cpu-line",
    subs: ["互联网", "人工智能", "硬件"],
  },
];

export const topics: Topic[] = [
  {
    id: "night-economy",
    title: "城市夜经济安全观察",
    intro: "当夜市、大排档与通宵街区成为城市消费新引擎，安全与规范的考题也随之而来。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20abstract%20illustration%20of%20a%20vibrant%20night%20market%20street%20with%20warm%20orange%20lanterns%20and%20deep%20blue%20evening%20sky%2C%20geometric%20minimal%20editorial%20poster%20style%2C%20high%20contrast%20dark%20tones%2C%20clean%20composition&width=1200&height=700&seq=topic-night-economy&orientation=landscape",
    reason: "本专题走访了 6 座城市的 18 个夜间消费聚集区，用现场调查回应“热闹背后安不安全”的公众关切。",
  },
  {
    id: "ai-landing",
    title: "AI 落地元年全景追踪",
    intro: "从办公助手到工厂质检，人工智能正在从概念走向真实的生产现场，机遇与泡沫并存。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20abstract%20illustration%20of%20glowing%20neural%20network%20nodes%20merging%20with%20industrial%20factory%20silhouette%2C%20warm%20amber%20and%20deep%20charcoal%20tones%2C%20minimal%20editorial%20poster%20style%2C%20high%20contrast%2C%20clean%20geometric%20composition&width=1200&height=700&seq=topic-ai-landing&orientation=landscape",
    reason: "我们采访了 12 家企业的技术负责人，试图回答一个朴素的问题：AI 到底帮企业多赚了还是多花了钱？",
  },
  {
    id: "tax-storm",
    title: "娱乐圈税务与合规风暴",
    intro: "从顶流被追缴到全行业自查，一场关于规则与底线的深层调整正在文娱行业展开。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20abstract%20editorial%20illustration%20of%20stage%20spotlight%20and%20audit%20documents%2C%20dramatic%20orange%20and%20deep%20blue%20contrast%2C%20minimal%20poster%20style%2C%20high%20contrast%20dark%20composition&width=1200&height=700&seq=topic-tax-storm&orientation=landscape",
    reason: "本专题不追八卦，只梳理公开信息与政策脉络，帮你读懂这场风暴的来龙去脉。",
  },
  {
    id: "consumer-rights",
    title: "消费维权进行时",
    intro: "预付跑路、隐私泄露、虚假宣传……当消费越便捷，维权也越需要方法。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20abstract%20illustration%20of%20a%20shield%20and%20receipt%20with%20warm%20orange%20highlights%20on%20soft%20cool%20gray%20background%2C%20minimal%20editorial%20poster%20style%2C%20clean%20geometric%20composition&width=1200&height=700&seq=topic-consumer-rights&orientation=landscape",
    reason: "整理真实投诉案例与可操作步骤，希望每一个普通消费者都能少走弯路。",
  },
];

export const articles: Article[] = [
  {
    id: "night-market-fire-safety",
    title: "深夜夜市燃气隐患调查：12 家摊位被连夜整改",
    summary:
      "记者连续一周走访市中心三条夜市街区，发现部分摊位存在软管老化、罐瓶超期等隐患，监管部门于凌晨集中执法并要求连夜整改。",
    content: [
      "凌晨一点，老城区的夜市依旧人声鼎沸。烤串的油烟、翻滚的麻辣烫锅、贴着地面的液化气软管，构成了这座街区最熟悉的深夜烟火气。然而在热闹之下，记者记录下了不少令人担忧的细节。",
      "在随机走访的 40 余个摊位中，有 12 家使用的燃气软管出现明显老化开裂，另有 6 家摊位的液化气罐已超过检验有效期。多位摊主坦言，平时忙着做生意，很少主动检查这些设备。",
      "接到反馈后，市场监管与消防救援部门于次日凌晨展开联合执法，对存在隐患的摊位现场下达整改通知，并要求在两日内更换合格设备。执法人员同时向周边商户发放了安全操作手册。",
      "一位参与执法的负责人表示，夜经济是城市活力的一部分，但安全从来没有例外。后续将建立夜市燃气设备定期巡检机制，把风险挡在事故发生之前。",
    ],
    quote: "夜经济越是热闹，安全这根弦越不能松——真正的城市温度，是既让你吃得开心，也不需要你提心吊胆。",
    category: "society",
    subCategory: "治安",
    author: "编辑部 · 陈默",
    date: "2026-10-11",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20bustling%20night%20street%20food%20market%20with%20warm%20orange%20string%20lights%20and%20deep%20blue%20evening%20atmosphere%2C%20moody%20documentary%20style%2C%20high%20detail%2C%20clean%20composition&width=800&height=600&seq=art-night-market-fire&orientation=landscape",
    tags: ["夜市", "安全", "燃气", "城市治理"],
    views: 24800,
    hot: true,
    topicIds: ["night-economy"],
  },
  {
    id: "community-parking-row",
    title: "老小区停车位之争：业主自发“划线”的背后",
    summary:
      "一个只有 180 个车位的老小区，却登记了 320 辆车。业主自发划线的举动，把长期存在的供需矛盾摆到了台面上。",
    content: [
      "上周，一组“业主深夜自发划线”的照片在本地社群刷屏。画面里，几十位居民打着手电，用反光漆在地面标出一个个车位。看似荒诞的举动，背后是长达数月的停车困局。",
      "小区建成于 2001 年，规划车位仅 180 个，如今登记车辆却超过 320 辆。晚归的居民常常要花半小时才能找到空位，消防通道被占用的投诉也屡见不鲜。",
      "物业表示，公共区域改造涉及规划、消防与费用分摊等多重问题，无法单方面推进。而业主们则希望先有一次公开、透明的协商，把规则一次性讲清楚。",
      "目前，社区居委会已介入协调，计划引入第三方机构评估地面改造方案。一位老住户说，大家要的其实不多，只是一个“回家能停下车”的确定感。",
    ],
    quote: "停车位的争夺，本质上是公共资源分配规则缺位的缩影。规则立起来，争吵才会少下去。",
    category: "society",
    subCategory: "民生",
    author: "编辑部 · 林望",
    date: "2026-10-09",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20an%20old%20residential%20courtyard%20with%20parked%20cars%20and%20freshly%20painted%20parking%20lines%2C%20soft%20overcast%20light%2C%20documentary%20style%2C%20clean%20composition%2C%20high%20detail&width=800&height=600&seq=art-community-parking&orientation=landscape",
    tags: ["停车", "社区", "民生", "公共资源"],
    views: 15600,
    hot: false,
    topicIds: ["night-economy"],
  },
  {
    id: "court-livestream",
    title: "全国首例庭审全程直播案落槌，法治透明再进一步",
    summary:
      "一起备受关注的合同纠纷案完成了从开庭到宣判的全程网络直播，累计观看人次突破 900 万，成为司法公开的新样本。",
    content: [
      "随着法槌落下，这场持续了三个小时的庭审宣告结束。与以往不同的是，从举证质证到宣判，每一个环节都被镜头完整记录并向公众开放。",
      "法院方面介绍，全程直播的目的不是制造话题，而是让司法过程本身成为最好的普法课堂。数据显示，直播期间累计观看人次突破 900 万，弹幕中“原来是这样”的评论反复出现。",
      "法律界人士评价，公开是最好的防腐剂。当程序被看见，当事人的权利与法官的裁量都处在阳光之下，公众对结果的信任也会随之提升。",
      "当然，直播也带来了新的课题，例如证人保护与隐私边界。相关部门表示，将根据不同案件类型细化公开规则，让透明与保护并行不悖。",
    ],
    quote: "把审判放到阳光下，不是作秀，而是让每一次裁判都经得起千万双眼睛的检验。",
    category: "society",
    subCategory: "法治",
    author: "编辑部 · 苏晴",
    date: "2026-10-07",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20modern%20courtroom%20interior%20with%20warm%20wood%20tones%20and%20a%20camera%20on%20tripod%2C%20soft%20directional%20light%2C%20solemn%20documentary%20style%2C%20clean%20composition&width=800&height=600&seq=art-court-livestream&orientation=landscape",
    tags: ["司法", "法治", "直播", "公开"],
    views: 31200,
    hot: true,
    topicIds: [],
  },
  {
    id: "school-bus-delay",
    title: "校车迟到两小时牵出承包黑幕，家长联名讨说法",
    summary:
      "一次看似偶然的迟到，让家长发现校车运营早已被层层转包。孩子们的安全，成了合同缝隙里最容易被忽视的一环。",
    content: [
      "那天早上，三十多个孩子在小区门口等了整整两个小时。校车迟迟未到，家长群里从焦急变成了愤怒。事后追查发现，运营公司早已把线路转包给第三方车队。",
      "多位家长向记者展示了合同复印件：中标公司负责签订协议，实际接送却由一家资质不明的车队承担，司机资质与车辆年检记录均存在疑点。",
      "教育部门回应称，已责令涉事公司暂停该线路运营，并对全区校车服务开展专项排查。相关负责人强调，学生的安全是不容转包的底线。",
      "一位家长代表说，他们不反对市场化，但反对把孩子的安全当成可以层层削薄的成本。家长联名信已提交，要求公开招标与运营全过程。",
    ],
    quote: "校车可以外包，责任不能外包。每一份合同背后，坐着的都是活生生的孩子。",
    category: "society",
    subCategory: "民生",
    author: "编辑部 · 何舟",
    date: "2026-10-05",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20yellow%20school%20bus%20parked%20on%20a%20quiet%20suburban%20road%20at%20dawn%2C%20soft%20warm%20light%2C%20documentary%20style%2C%20clean%20minimal%20composition&width=800&height=600&seq=art-school-bus&orientation=landscape",
    tags: ["校车", "教育", "安全", "合同"],
    views: 12400,
    hot: false,
    topicIds: ["night-economy"],
  },
  {
    id: "flood-response-drill",
    title: "暴雨红色预警下，这座城市做了三件关键的事",
    summary:
      "面对近年来最强的一轮降雨，当地用提前预警、下沉干部和弹性停工三道防线，把损失压到了最低。",
    content: [
      "天气预报发出红色预警的那一刻，城市应急系统便已启动。与往年相比，这一轮应对显得更加从容，而从容背后是三件做在前面的事。",
      "第一是预警前置。气象、水务与交通部门的数据被打通，内涝风险点提前 6 小时向社会公布，市民得以错峰出行。第二是干部下沉，街道工作人员在暴雨来临前就驻守到易涝小区。",
      "第三是弹性机制。用人单位被鼓励灵活安排通勤，学校视情况停课，把“人”的安全放在了效率之前。数据显显示，本轮降雨期间未发生人员伤亡。",
      "一位应急专家指出，真正的应急能力，不在于灾后救援多壮观，而在于灾前把多少人从风险里挪了出来。",
    ],
    quote: "最好的救灾，是让灾害根本没机会成为灾难。",
    category: "society",
    subCategory: "治安",
    author: "编辑部 · 陈默",
    date: "2026-10-02",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20rainy%20city%20street%20with%20reflections%20and%20warm%20street%20lamps%2C%20moody%20blue%20and%20orange%20tones%2C%20documentary%20style%2C%20clean%20composition&width=800&height=600&seq=art-flood-response&orientation=landscape",
    tags: ["暴雨", "应急", "城市", "防灾"],
    views: 9800,
    hot: false,
    topicIds: ["night-economy"],
  },
  {
    id: "idol-tax-evasion",
    title: "顶流艺人偷逃税被追缴，行业再掀自查风暴",
    summary:
      "税务部门通报一起文娱领域重大偷逃税案件，涉案艺人被依法追缴税款及滞纳金，多个工作室随即启动自查。",
    content: [
      "通报发布当晚，相关话题迅速登上热搜。根据官方信息，该艺人在数年间通过拆分收入、虚构业务等方式隐匿个人所得，最终被依法追缴税款、滞纳金并处罚款。",
      "业内人士透露，案件曝光后，多家经纪公司与艺人工作室连夜召开内部会议，排查过往合同与资金往来。合规，正在从“可选项”变成行业的生存底线。",
      "值得注意的是，这并非孤例。近年来，文娱行业的税务监管持续加强，从阴阳合同到拆分收入，一条条灰色路径正被逐一堵上。",
      "有律师提醒，艺人作为高收入群体，依法纳税既是义务也是最好的风险对冲。与其事后补救，不如在签约第一笔收入时就规范到底。",
    ],
    quote: "流量可以带来收入，但带来不了一切。规则面前，没有顶流，只有纳税人。",
    category: "entertainment",
    subCategory: "明星",
    author: "文娱组 · 沈亦",
    date: "2026-10-10",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20an%20empty%20concert%20stage%20with%20warm%20spotlight%20and%20deep%20blue%20backdrop%2C%20dramatic%20moody%20lighting%2C%20minimal%20composition%2C%20high%20detail&width=800&height=600&seq=art-idol-tax&orientation=landscape",
    tags: ["税务", "明星", "文娱", "合规"],
    views: 42800,
    hot: true,
    topicIds: ["tax-storm"],
  },
  {
    id: "summer-film-battle",
    title: "暑期档票房混战：三部大片改档背后的博弈",
    summary:
      "三部原本同期上映的影片接连宣布改档，票房大战背后，是排片、口碑与资本的多重角力。",
    content: [
      "今年的暑期档格外拥挤。原本定档同一天的三部大片，在映前两周接连宣布调整上映时间，引发业内外的广泛猜测。",
      "一位资深发行人坦言，改档往往不是“怕”，而是“算”。同一档期如果题材重叠，观众注意力被稀释，口碑稍逊的一方很容易票房腰斩。",
      "改档也有代价。宣传物料、媒体投放、主创档期都要重新协调，一次调整背后是数千万元的成本。但比起硬碰硬，许多片方宁愿选择更有利的窗口期。",
      "对观众而言，几部好片错峰上映其实是好事。只是当档期成为博弈工具，那些真正需要被看见的小成本作品，往往更容易被挤出局。",
    ],
    quote: "档期是市场的一部分，但当所有人都忙着算档期，还有谁在算作品本身？",
    category: "entertainment",
    subCategory: "影视",
    author: "文娱组 · 叶栖",
    date: "2026-10-08",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20classic%20cinema%20marquee%20glowing%20at%20dusk%20with%20warm%20bulbs%20and%20deep%20blue%20sky%2C%20cinematic%20moody%20atmosphere%2C%20clean%20composition&width=800&height=600&seq=art-summer-film&orientation=landscape",
    tags: ["电影", "票房", "档期", "影视"],
    views: 18700,
    hot: false,
    topicIds: [],
  },
  {
    id: "singer-lip-sync",
    title: "演唱会假唱风波升级，主办方连夜公布音轨",
    summary:
      "一段现场视频引发假唱质疑，主办方紧急公开多轨音源自证，事件也把演出行业的真唱标准推上讨论桌。",
    content: [
      "一段现场观众拍摄的视频在网络上迅速传播，画面与声音的细微不同步，让“假唱”的质疑声不断放大。24 小时内，话题阅读量突破五亿。",
      "面对汹涌的舆论，主办方连夜召开发布会，公开了现场多轨音源与调音台截图，并邀请第三方音频团队进行技术说明，称部分段落使用了导唱伴音以便完成高难度编舞。",
      "争议并未就此平息。业内人士指出，演唱会是否真唱，目前缺乏统一的行业标准，观众往往只能凭观感判断，这就给了模糊空间。",
      "无论如何，这场风波至少说明了一件事：观众愿意为真诚的现场买单，却很难接受被当成不懂行的“外行”。",
    ],
    quote: "观众花上千块买一张票，要的不是完美无瑕的录音室版本，而是此刻真实的声音。",
    category: "entertainment",
    subCategory: "音乐",
    author: "文娱组 · 叶栖",
    date: "2026-10-04",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20microphone%20on%20a%20concert%20stage%20with%20warm%20stage%20lights%20and%20deep%20blue%20haze%2C%20dramatic%20cinematic%20lighting%2C%20minimal%20composition&width=800&height=600&seq=art-singer-live&orientation=landscape",
    tags: ["演唱会", "音乐", "假唱", "现场"],
    views: 26400,
    hot: true,
    topicIds: ["tax-storm"],
  },
  {
    id: "variety-show-edit",
    title: "综艺剪辑争议：嘉宾镜头被删引发粉丝维权",
    summary:
      "一期节目的镜头分配被指严重失衡，粉丝集体维权，节目组回应称剪辑需服从整体节奏，双方各执一词。",
    content: [
      "节目播出当晚，社交平台上便出现了大量质疑。多位观众统计后发现，某位嘉宾的正面镜头不足三十秒，而预告片中却占了不小的篇幅。",
      "粉丝随即发起集体维权，整理出声画对比材料，要求节目组给出解释。节目组回应称，成片剪辑需要考虑整体叙事与时长，个别调整属于正常制作流程。",
      "一位剪辑师向记者透露，综艺成片受广告、时长、话题度等多重因素影响，嘉宾“高开低走”并不罕见，但观众的感受同样值得被认真对待。",
      "这场争议折射出的，是内容生产与观众期待之间的张力。当观众越来越投入，他们对“被尊重”的要求也在水涨船高。",
    ],
    quote: "剪辑是创作，但创作的前提是诚实。观众可以接受取舍，却难以接受被愚弄。",
    category: "entertainment",
    subCategory: "影视",
    author: "文娱组 · 沈亦",
    date: "2026-10-01",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20television%20editing%20timeline%20on%20a%20screen%20with%20warm%20accent%20highlights%20in%20a%20dark%20studio%2C%20cinematic%20moody%20style%2C%20clean%20composition&width=800&height=600&seq=art-variety-edit&orientation=landscape",
    tags: ["综艺", "剪辑", "粉丝", "影视"],
    views: 14200,
    hot: false,
    topicIds: ["tax-storm"],
  },
  {
    id: "ai-agent-enterprise",
    title: "AI Agent 进入企业办公，效率神话还是新一轮泡沫？",
    summary:
      "越来越多企业把 AI 智能体接入日常流程，但真实收益究竟几何？我们走访了 12 家公司，听到两种截然不同的声音。",
    content: [
      "走进这家制造企业的质检车间，一排摄像头正以肉眼难辨的速度扫描产品瑕疵。负责人说，自从引入 AI 质检，漏检率下降了大半。",
      "但并非所有故事都这么美好。另一家公司的技术主管坦言，他们花了大半年部署的办公智能体，最终因为“回答不够靠谱”而被员工弃用。",
      "多位受访者提到同一个关键词：数据。AI 的能力高度依赖数据质量，而许多企业的历史数据分散、标准不一，直接拖累了智能体的表现。",
      "行业分析师认为，2026 年更像是“挤泡沫”的一年。真正能跑通的企业，未必用了最炫的模型，但一定把数据和流程先理顺了。",
    ],
    quote: "AI 不会自动带来效率，它更像一面镜子，照出的是企业原本的管理水平。",
    category: "tech",
    subCategory: "人工智能",
    author: "科技组 · 顾一鸣",
    date: "2026-10-11",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20modern%20office%20with%20a%20glowing%20holographic%20data%20interface%20and%20warm%20accent%20lighting%2C%20minimal%20tech%20aesthetic%2C%20clean%20composition%2C%20high%20detail&width=800&height=600&seq=art-ai-agent&orientation=landscape",
    tags: ["人工智能", "企业", "效率", "数字化转型"],
    views: 33900,
    hot: true,
    topicIds: ["ai-landing"],
  },
  {
    id: "ev-battery-recall",
    title: "某品牌电动车电池批量召回，起火隐患引关注",
    summary:
      "因电芯一致性问题可能引发热失控，某品牌宣布召回数万辆电动车，免费更换电池模组。",
    content: [
      "召回公告显示，本次涉及车辆约 3.2 万辆，问题主要集中在某批次电芯的一致性偏差上。在极端情况下，这些电芯可能引发热失控，存在起火风险。",
      "公司表示，将为受影响车主免费检测并更换电池模组，同时通过远程诊断提前识别高风险车辆。车主可通过官方渠道查询自己的车辆是否在召回范围内。",
      "这并非新能源行业第一次面对电池安全问题。随着保有量快速增长，从材料到系统管理的全链条安全，正成为所有厂商绕不开的必修课。",
      "一位行业工程师提醒，消费者无需过度恐慌，但应重视车辆的异常提示，及时送检。安全从来不是某一家的责任，而是整个产业链的共同底线。",
    ],
    quote: "电动车跑得再快，也快不过安全这两个字的分量。",
    category: "tech",
    subCategory: "硬件",
    author: "科技组 · 顾一鸣",
    date: "2026-10-07",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20an%20electric%20car%20battery%20module%20assembly%20line%20with%20warm%20industrial%20lighting%20and%20clean%20minimal%20background%2C%20high%20detail%20tech%20aesthetic&width=800&height=600&seq=art-ev-battery&orientation=landscape",
    tags: ["电动车", "电池", "召回", "安全"],
    views: 21500,
    hot: false,
    topicIds: ["ai-landing"],
  },
  {
    id: "short-video-bubble",
    title: "短剧出海狂飙：月流水破亿背后的灰色地带",
    summary:
      "短剧出海成为新的造富神话，但在高额流水之下，版权、分成与内容合规的隐忧正在浮现。",
    content: [
      "一部成本不足百万的短剧，海外月流水却能做到上亿。这样的故事在过去一年里不断上演，吸引了大量团队涌入这条赛道。",
      "但在光鲜的数据背后，是复杂的利益分配。多位从业者透露，平台、投流方与制作方的分成比例并不透明，真正能拿到手的利润远没有传说中那么高。",
      "与此同时，内容同质化与版权争议也日益突出。为了抢速度，一些团队直接翻拍热门套路，原创者往往难以维权。",
      "有投资人判断，短剧出海正从“野蛮生长”走向“精耕细作”。能留下来的，将是那些真正理解本地文化、又守住内容底线的团队。",
    ],
    quote: "风口上的流水很性感，但风口过去后，能站住的永远是内容和合规。",
    category: "tech",
    subCategory: "互联网",
    author: "科技组 · 白露",
    date: "2026-10-03",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20smartphone%20showing%20vertical%20video%20content%20on%20a%20desk%20with%20warm%20accent%20lighting%20and%20clean%20minimal%20background%2C%20high%20detail&width=800&height=600&seq=art-short-video&orientation=landscape",
    tags: ["短剧", "出海", "互联网", "版权"],
    views: 17300,
    hot: false,
    topicIds: ["consumer-rights"],
  },
  {
    id: "chip-export-saga",
    title: "国产芯片量产突破，供应链自主再进一步",
    summary:
      "某国产先进制程芯片宣布进入规模量产，多家终端厂商完成适配，供应链自主化迈出关键一步。",
    content: [
      "在近日的一场技术发布会上，厂商宣布其新一代芯片已实现规模量产，良率与性能均达到预期目标。多家终端设备厂商同步展示了适配成果。",
      "业内人士指出，从实验室走向量产，是芯片行业最难跨越的一道坎。良率、产能与生态适配，任何一环掉链子都会让整条路线功亏一篑。",
      "这也意味着，长期依赖进口的高端供应格局正在被一点点改写。不过专家提醒，技术突破不意味着可以松懈，持续投入基础研发仍是长期课题。",
      "“真正的自主，不是关起门来，而是在开放中拥有选择权。”一位从业者这样形容当下的意义。",
    ],
    quote: "芯片的突破从来不是一夜之间，而是无数个良率数字背后，工程师们一点一点磨出来的。",
    category: "tech",
    subCategory: "硬件",
    author: "科技组 · 白露",
    date: "2026-09-30",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20a%20semiconductor%20wafer%20held%20under%20warm%20clean%20lab%20lighting%20with%20deep%20blue%20accents%2C%20high%20detail%20tech%20aesthetic%2C%20minimal%20composition&width=800&height=600&seq=art-chip&orientation=landscape",
    tags: ["芯片", "半导体", "制造", "自主"],
    views: 28700,
    hot: true,
    topicIds: ["ai-landing"],
  },
  {
    id: "social-platform-privacy",
    title: "社交平台隐私新规落地，你的数据谁做主？",
    summary:
      "新规要求平台在收集个人信息前进行更明确的告知，用户的知情权与选择权被提到了更显眼的位置。",
    content: [
      "打开任何一款社交应用，第一屏往往是冗长的隐私条款。新规落地后，这种“一签了之”的局面正在被改变。",
      "按照规定，平台需要在收集敏感信息前进行单独告知，并提供便捷的关闭选项。用户不再需要在一堆专业术语里自己找出“同意”按钮藏在哪里。",
      "有产品经理表示，新规短期内会增加合规成本，但长期看会让行业更健康。“把选择权还给用户，其实也是在重建信任。”",
      "当然，规则的生命力在于执行。多位专家建议，用户也应主动了解自己的权利，遇到过度收集时及时投诉，让监督成为常态。",
    ],
    quote: "隐私不是平台赐予的礼物，而是每一个用户本就应该拥有的权利。",
    category: "tech",
    subCategory: "互联网",
    author: "科技组 · 顾一鸣",
    date: "2026-09-28",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20padlock%20and%20digital%20data%20streams%20with%20warm%20orange%20highlights%20on%20cool%20gray%20background%2C%20minimal%20tech%20aesthetic%2C%20clean%20composition&width=800&height=600&seq=art-privacy&orientation=landscape",
    tags: ["隐私", "数据", "社交平台", "合规"],
    views: 13600,
    hot: false,
    topicIds: ["consumer-rights"],
  },
  {
    id: "prepaid-gym-run",
    title: "预付式健身房跑路潮：办卡的钱还能追回来吗？",
    summary:
      "又一批健身房突然闭店，会员预交的费用打了水漂。预付式消费的风险，再次被推到风口浪尖。",
    content: [
      "上周五还在正常营业的门店，周一就贴出了“场地调整”的告示。数百名会员发现，联系不上负责人，预付的会员费也无处追讨。",
      "这类事件近年来反复上演。预付式消费的本质，是消费者提前为尚未获得的服务买单，一旦商家资金链断裂，风险几乎全部由用户承担。",
      "法律界人士建议，消费者办卡前应查看商家的经营资质与合同条款，尽量选择小额、短期套餐，并保留好付款凭证，以便维权时使用。",
      "监管部门表示，正在推动预付资金存管制度，让消费者的钱先“存起来”，根据实际服务进度划转，从源头降低跑路风险。",
    ],
    quote: "预付的不只是钱，更是信任。当信任被透支，整个行业都会为之买单。",
    category: "society",
    subCategory: "民生",
    author: "编辑部 · 林望",
    date: "2026-09-26",
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20photo%20of%20an%20empty%20gym%20with%20abandoned%20equipment%20and%20a%20closed%20sign%2C%20cool%20moody%20lighting%20with%20warm%20accent%2C%20documentary%20style%2C%20clean%20composition&width=800&height=600&seq=art-gym-run&orientation=landscape",
    tags: ["预付", "消费维权", "健身", "民生"],
    views: 19200,
    hot: false,
    topicIds: ["consumer-rights"],
  },
];